/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  InputAdornment,
  IconButton,
  DialogContent,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import { 
  StyledTitle,
  StyledSubTitle,
  StyledFormContainer,
  StyledPasswordField,
  StyledResetButton,
  StyledHelperText,
  StyledDialog,
  StyledErrorTitleContainer,
  StyledDialogTitle,
  StyledList,
  ResetPasswordWrapper,
  StyledBackToLoginButton,
  StyledInstructions,
  FormFieldTitle,
  CarouselContainer,
  FormContainer,
  FormWrapper,
  PasswordInstructionTitle,
  PasswordErrorText,
  PasswordDescriptionTitle,
} from "./styles";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { LoadingSpinner } from "../../components/loading_spinner";
import api from "../../api";
import { CarouselPage } from "../../components/carousel";
import banner from '../../assets/images/banner.jpg';

export const ResetForgotPassword = () => {
  const { token } = useParams()
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false)
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [error, setError] = React.useState({})
  const [status, setStatus] = React.useState(false)
  const [errorOpen, setErrorOpen] = React.useState(false)
  const [successOpen, setSuccessOpen] = React.useState(false)
  const [newPasswordCheck, setNewPasswordCheck] = React.useState(true)
  const [confirmPasswordCheck, setConfirmPasswordCheck] = React.useState(true)
  const carouselData = [
    {
      id: 1,
      image: banner,
    },
    {
      id: 2,
      image: banner,
    },
    {
      id: 3,
      image: banner,
    },
  ]

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show)

  const handleNewPassword = (value) => {
    setNewPassword(value)
    if(error?.new_password){
      setError({...error, new_password: ''})
    }
    if(value) {
      setNewPasswordCheck(false)
    }
    else {
      setNewPasswordCheck(true)
    }
  }

  const handleConfirmPassword = (value) => {
    setConfirmPassword(value)
    if(error?.confirm_password){
      setError({...error, confirm_password: ''})
    }
    if(value) {
      setConfirmPasswordCheck(false)
    }
    else {
      setConfirmPasswordCheck(true)
    }
  }

  const handleClose = () => {
    setErrorOpen(false)
    setError('')
  }

  const handleSuccessClose = () => {
    setSuccessOpen(false)
    navigate('/')
  }

  const handleResetPassword = async () => {
    setStatus(true)
    await api.put('/v1/admin/reset_forgot_password',{ token: token, new_password: newPassword, confirm_password: confirmPassword })
    .then(() => {
      setStatus(false)
      setSuccessOpen(true)
    })
    .catch((error) => {
      const field = Object.keys(error?.response?.data?.errors)
      if(field[0] != 'base'){
        setError(error?.response?.data?.errors)
      }
      else{
        const issue = error?.response?.data?.errors?.base
        if(issue){
          setError(issue[0])
          setErrorOpen(true)
        }
      }
      setStatus(false)
    })
  }

  return (
    <React.Fragment>
      <div>
        {/* Success Alert */}
        <Snackbar
          open={successOpen}
          autoHideDuration={5000}
          onClose={handleSuccessClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            onClose={handleSuccessClose}
            severity="success"
            variant="filled"
          >
            Password updated successfully
          </Alert>
        </Snackbar>

        {/* Error Alert */}
        <StyledDialog
          open={errorOpen}
          onClose={handleClose}
        >
          <StyledErrorTitleContainer>
            <ReportProblemIcon style={{fontSize: '35px', color: 'red'}}/>
            <StyledDialogTitle>Error for password</StyledDialogTitle>
          </StyledErrorTitleContainer>

          <DialogContent>
            <PasswordErrorText>{error}</PasswordErrorText>
            <PasswordDescriptionTitle>
              Password must:
            </PasswordDescriptionTitle>
            <StyledList>
              <li>have at least 8 characters</li>
              <li>have a capital letter</li>
              <li>have a small letter</li>
              <li>have a number</li>
            </StyledList>
          </DialogContent>
        </StyledDialog>
        
        {/* Reset  Form Page*/}
        <ResetPasswordWrapper>
          <CarouselContainer>
            <CarouselPage data={carouselData} />
          </CarouselContainer>

          <FormContainer>
            <FormWrapper>
              <StyledTitle>Reset Password</StyledTitle>
              <StyledSubTitle>Your new password must be different from previously used passwords.</StyledSubTitle>

              <Divider style={{borderStyle: 'solid', borderWidth: '2px', marginBottom: '20px', backgroundColor: '#252525'}}/>

              <StyledFormContainer>
                <div>
                  <FormFieldTitle>Create new password</FormFieldTitle>
                  <StyledPasswordField
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    onChange={(e) => handleNewPassword(e.target.value)}
                    error={error?.new_password}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton onClick={handleClickShowPassword}
                        >
                          { showPassword ? <VisibilityOff /> : <Visibility /> }
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <StyledHelperText>{ error?.new_password && `${error?.new_password}` }</StyledHelperText>
                </div>

                <div style={{marginTop: '10px'}}>
                  <FormFieldTitle>Confirm new password</FormFieldTitle>
                  <StyledPasswordField
                    type={ showConfirmPassword ? 'text' : 'password' }
                    placeholder="Enter confirm password"
                    onChange={(e) => handleConfirmPassword(e.target.value)}
                    error={error?.confirm_password}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton onClick={handleClickShowConfirmPassword}>
                          { showConfirmPassword ? <VisibilityOff /> : <Visibility />  }
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <StyledHelperText>{ error?.confirm_password && `${error?.confirm_password}` }</StyledHelperText>
                </div>
                <div>
                  <PasswordInstructionTitle>Password must:</PasswordInstructionTitle>
                  <StyledInstructions>
                    <li>have at least 8 characters</li>
                    <li>have atleast 1 number</li>
                    <li>have atleast 1 letter</li>
                    <li>contain no spaces</li>
                  </StyledInstructions>
                </div>
                <div style={{display: 'flex', gap: '20px', justifyContent: 'center'}}>
                  <StyledBackToLoginButton 
                    variant="outlined"
                    onClick={() => navigate('/')}
                  >
                    Cancel
                  </StyledBackToLoginButton>
                  <StyledResetButton 
                    onClick={handleResetPassword}
                    disabled={status || (newPasswordCheck || confirmPasswordCheck)}
                  >
                    { status ? <LoadingSpinner isLoading={true} /> : 'Update Password' }
                  </StyledResetButton>
                </div>
              </StyledFormContainer>
            </FormWrapper>
          </FormContainer>
        </ResetPasswordWrapper>
      </div>
    </React.Fragment>
  )
}
